import {
  RiInstagramLine,
  RiLinkedinBoxLine,
  RiTiktokFill,
  RiTwitterXLine,
} from "@remixicon/react"
import { Link } from "react-router-dom"

import { Button } from "@/components/ui/button"
import { useAppSession } from "@/hooks/useAppSession"
import { APP_REGISTER_URL, BLOG_URL, TIKTOK_URL } from "@/lib/site"

type FooterLink = {
  label: string
  to?: string
  href?: string
}

const company: FooterLink[] = [
  { to: "/company", label: "About" },
  { to: "/team", label: "Team" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
  { href: "https://sqaleupinc.com", label: "Sqaleup Inc." },
]

const product: FooterLink[] = [
  { to: "/features", label: "Features" },
  { to: "/product", label: "Product" },
  { to: "/product/sales-link", label: "Your sales link" },
  { to: "/product/sell", label: "Sell & checkout" },
  { to: "/product/grow", label: "Grow with insight" },
  { to: "/product/connect", label: "Stay connected" },
  { to: "/pricing", label: "Pricing" },
  { to: "/solutions", label: "Solutions" },
  { to: "/solutions/social-sellers", label: "Social sellers" },
  { to: "/solutions/fashion-lifestyle", label: "Fashion & lifestyle" },
  { to: "/solutions/food-beverage", label: "Food & beverage" },
  { to: "/solutions/services-bookings", label: "Services & bookings" },
]

const support: FooterLink[] = [
  { href: APP_REGISTER_URL, label: "Getting started" },
  { href: BLOG_URL, label: "The Sqaleshop Blog" },
  { to: "/features", label: "Features & how-tos" },
  { to: "/#faq", label: "FAQs" },
  { to: "/contact", label: "Contact" },
]

const legal: FooterLink[] = [
  { to: "/terms", label: "Terms & Conditions" },
  { to: "/privacy", label: "Privacy policy" },
  { to: "/merchant-agreement", label: "Merchant agreement" },
  { to: "/compliance", label: "Compliance" },
]

const columns = [
  { title: "Company", links: company },
  { title: "Product", links: product },
  { title: "Support", links: support },
  { title: "Trust & Legal", links: legal },
] as const

const social = [
  { href: "https://x.com/sqaleshop", label: "X", Icon: RiTwitterXLine },
  { href: "https://instagram.com/sqaleshop", label: "Instagram", Icon: RiInstagramLine },
  { href: TIKTOK_URL, label: "TikTok", Icon: RiTiktokFill },
  {
    href: "https://www.linkedin.com/company/sqaleshop",
    label: "LinkedIn",
    Icon: RiLinkedinBoxLine,
  },
] as const

function FooterLinkItem({ item }: { item: FooterLink }) {
  const className =
    "text-[14px] leading-snug text-gray-600 font-medium transition-colors hover:text-foreground"

  if (item.href) {
    const external = item.href.startsWith("http")
    return (
      <a
        href={item.href}
        className={className}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {item.label}
      </a>
    )
  }

  return (
    <Link to={item.to!} className={className}>
      {item.label}
    </Link>
  )
}

function FooterCol({ title, links }: { title: string; links: readonly FooterLink[] }) {
  return (
    <div>
      <p className="text-xl lg:text-2xl font-bold font-heading tracking-tight text-foreground">{title}</p>
      <ul className="mt-5 space-y-3">
        {links.map((item) => (
          <li key={`${title}-${item.label}`}>
            <FooterLinkItem item={item} />
          </li>
        ))}
      </ul>
    </div>
  )
}

export function SiteFooter() {
  const { signedIn, startHref, startLabel } = useAppSession()
  const gettingStarted = signedIn
    ? { to: startHref, label: startLabel }
    : { href: APP_REGISTER_URL, label: "Getting started" }
  const supportLinks: FooterLink[] = [
    gettingStarted,
    { href: BLOG_URL, label: "The Sqaleshop Blog" },
    { to: "/features", label: "Features & how-tos" },
    { to: "/#faq", label: "FAQs" },
    { to: "/contact", label: "Contact" },
  ]

  return (
    <footer className="bg-primary px-4 pb-8 pt-4 sm:px-6">
      <div className="mx-auto max-w-[96%] lg:max-w-[80%]">
        <div className="rounded-[2rem] bg-card px-8 py-10 shadow-xl sm:px-10 sm:py-12 lg:px-14 lg:py-14">
          <nav
            aria-label="Footer"
            className="grid grid-cols-2 gap-x-8 gap-y-10 sm:gap-x-12 lg:grid-cols-4 lg:gap-x-10"
          >
            {columns.map((col) => (
              <FooterCol
                key={col.title}
                title={col.title}
                links={col.title === "Support" ? supportLinks : col.links}
              />
            ))}
          </nav>

          <div className="mt-12 flex flex-col gap-8 sm:mt-4 sm:pt-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              {signedIn ? (
                <Button
                  size="lg"
                  className="h-12 rounded-full px-7 text-base font-semibold"
                  asChild
                >
                  <Link to={startHref}>{startLabel}</Link>
                </Button>
              ) : (
                <>
                  <Button
                    size="lg"
                    className="h-12 rounded-lg bg-[#f0f0f0] px-7 text-base font-semibold text-foreground shadow-none hover:bg-[#e4e4e4] hover:text-foreground"
                    asChild
                  >
                    <Link to="/login">Log in</Link>
                  </Button>
                  <Button
                    size="lg"
                    className="h-12 rounded-full px-7 text-base font-semibold"
                    asChild
                  >
                    <Link to={APP_REGISTER_URL}>Get started for free</Link>
                  </Button>
                </>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              {social.map(({ href, label, Icon }) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex size-11 items-center justify-center rounded-full bg-foreground text-background transition-opacity hover:opacity-80"
                  aria-label={label}
                >
                  <Icon className="size-5" />
                </a>
              ))}
            </div>
          </div>

          <div className="mt-8 text-xs leading-relaxed font-semibold text-muted-foreground sm:mt-10 text-center">
            <p>© {new Date().getFullYear()} Sqaleup Inc. All rights reserved.</p>
            <p className="mt-0.5">
              Sqaleshop is a product of{" "}
              <a
                href="https://sqaleupinc.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground/70 transition-colors hover:text-foreground"
              >
                Sqaleup Inc.
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}
